import type { IconNames } from '@workramp/ui';

import { useMemo, useState, Fragment, useEffect } from 'react';
import {
  TextField,
  CheckboxGroup,
  CheckboxGroupItem,
  CheckboxGroupSection,
  MultiSelect,
  Body,
  Stack,
  Icon,
  Col,
  P,
  Row,
  Divider,
} from '@workramp/ui';
import { I18n } from '~/common/explicit-exports';
import { SearchContainer, ClearSearchIcon } from './FilterMultiSelect.styles';

export type Option = {
  label: string;
  suffix?: string;
  value: string;
  iconName?: IconNames;
  iconColor?: string;
  autoFocus?: boolean;
};

export type OptionGroup = {
  id: string;
  label?: string;
  items: Option[];
};

type FilterMultiSelectProps = {
  label: string;
  optionsData: OptionGroup[];
  value: string[];
  onChange: (selectedOptions: string[]) => void;
  onClear: () => void;
  enableSearch?: boolean;
  searchCallback?: (value: string) => void;
  isDisabled?: boolean;
  isLoading?: boolean;
  showSectionDividers?: boolean;
  size?: 'sm' | 'md';
};

// FilterMultiSelectCheckboxGroupItem component creates a CheckboxGroupItem
// based on the given properties of an Option
const FilterMultiSelectCheckboxGroupItem = ({
  value: itemValue,
  suffix,
  iconName,
  iconColor,
  label: itemLabel,
  autoFocus,
}: Option) => (
  <CheckboxGroupItem key={itemValue} suffix={suffix} value={itemValue} autoFocus={autoFocus}>
    <Row align="center" gapX={2}>
      <Col xs="auto">
        {iconName && (
          <div data-testid={`icon-${iconName}`}>
            <Icon name={iconName} color={iconColor} />
          </div>
        )}
      </Col>
      <Col>
        <P size={1}>{itemLabel}</P>
      </Col>
    </Row>
  </CheckboxGroupItem>
);

export const FilterMultiSelect = ({
  label,
  optionsData,
  value,
  onClear,
  onChange,
  enableSearch,
  searchCallback,
  isDisabled,
  isLoading,
  showSectionDividers,
  size = 'md',
}: FilterMultiSelectProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false); // Track search input focus
  const [firstItemValue, setFirstItemValue] = useState<string | null>(null); // State to store the first item's value

  const NO_RESULTS_FOUND = I18n.t('js.learner.search.no_results_found');
  const LOADING = I18n.t('js.learner.search.loading');

  // Handle the TAB key to focus on the first option if present
  const handleKeyDown = (event) => {
    // If the search input is focused and the TAB key is pressed or space key is pressed with an empty search value
    if ((event.key === 'Tab' && isSearchFocused) || (event.key === ' ' && searchValue === '')) {
      event.preventDefault(); // Prevent default tab behavior

      // Use the firstItemValue state to find the first item by `value`
      const firstOption = document.querySelector(`[value="${firstItemValue}"]`) as HTMLElement;

      if (firstOption) {
        firstOption.focus(); // Focus the first option if available
      }
    }
  };

  // sections is a memoized array of option groups, filtered by search string
  const sections = useMemo(() => {
    if (searchValue === '') {
      return optionsData;
    }

    return optionsData.map((optionGroup) => ({
      ...optionGroup,
      items: optionGroup.items.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase())),
    }));
  }, [searchValue, optionsData]);

  // Set the first item's value whenever the sections are updated
  useEffect(() => {
    if (sections[0]?.items?.length > 0) {
      setFirstItemValue(sections[0].items[0]?.value); // Store the first item's value
    } else {
      setFirstItemValue(sections[1]?.items[0]?.value); // Store the first item's value
    }
  }, [sections]);

  // If there's a search string, filter items by it
  const children = useMemo(
    () =>
      sections.length > 1
        ? sections.map(
            (section, i) =>
              section.items?.length > 0 && (
                <Fragment key={section.id}>
                  <CheckboxGroupSection key={section.id} title={section.label ?? ''}>
                    {section.items.map((item) => (
                      <FilterMultiSelectCheckboxGroupItem
                        value={item.value}
                        label={item.label}
                        iconName={item.iconName}
                        iconColor={item.iconColor}
                        suffix={item.suffix}
                        key={item.value}
                        data-testid={`checkbox-group-item-${item.value}`}
                      />
                    ))}
                  </CheckboxGroupSection>
                  {showSectionDividers && i < sections.length - 1 && section.items.length > 0 && (
                    <Divider elementType="li" spacing={4} />
                  )}
                </Fragment>
              ),
          )
        : sections[0]?.items?.map((item) => (
            <FilterMultiSelectCheckboxGroupItem
              value={item.value}
              label={item.label}
              iconName={item.iconName}
              iconColor={item.iconColor}
              suffix={item.suffix}
              key={item.value}
            />
          )),
    [sections, showSectionDividers],
  );

  const hasResults = children?.some(Boolean);
  return (
    <MultiSelect
      label={label}
      title={label}
      onClear={onClear}
      numSelections={value.length}
      isDisabled={isDisabled}
      size={size}
    >
      <Stack size={4}>
        {enableSearch && (
          <SearchContainer>
            <TextField
              data-testid="search-input"
              hideLabel
              label="search"
              leadingIconName="search:regular"
              placeholder="Search"
              size="sm"
              value={searchValue}
              onFocus={() => setIsSearchFocused(true)} // Set search as focused
              onBlur={() => setIsSearchFocused(false)} // Reset search focus state on blur
              onKeyDown={handleKeyDown} // Handle TAB keypress
              autoFocus
              onChange={(newSearchValue: string) => {
                searchCallback?.(newSearchValue);
                setSearchValue(newSearchValue);
              }}
            />
            {searchValue ? (
              <ClearSearchIcon
                data-testid="clear-search-icon"
                color="secondary"
                variant="text"
                size="sm"
                label="Clear Search"
                iconName="x-small:regular"
                onPress={() => {
                  searchCallback?.('');
                  setSearchValue('');
                }}
              />
            ) : null}
          </SearchContainer>
        )}

        {hasResults && (
          <CheckboxGroup hideLabel label={label} value={value} onChange={onChange}>
            {children}
          </CheckboxGroup>
        )}
        {isLoading && <Body level="md">{LOADING}</Body>}
        {!hasResults && !isLoading && <Body level="md">{NO_RESULTS_FOUND}</Body>}
      </Stack>
    </MultiSelect>
  );
};
